// Core turn-based game types (Warring Isles v1). Plain data, no Pixi, no DOM.
// A "region" is one land hex; regions group into islands; factions are the races.

export type FactionId = number;
export type RegionId = number;

export const NEUTRAL: FactionId = -1; // unowned region

export const RESOURCES = 4;
export const RES_ICON = ['🪙', '🌲', '🪨', '🌾'] as const; // Gold, Wood, Stone, Food
export const RES_NAME = ['Gold', 'Wood', 'Stone', 'Food'] as const;

// Buildings. Market boosts income; Harbor unlocks sea assaults from its island (isles maps) and the
// Barracks is the mainland's supply building (land maps): each raises the army cap by +1. Wall
// reduces incoming combat damage where present (scenario dressing; not player-buildable).
export const BUILDING = { Market: 0, Barracks: 1, Wall: 2, Harbor: 3 } as const;
export type BuildingType = (typeof BUILDING)[keyof typeof BUILDING];
export const BUILDING_ICON = ['🏛️', '🛡️', '🧱', '⚓'] as const;
export const BUILDING_NAME = ['Market', 'Barracks', 'Wall', 'Harbor'] as const;

// Units: 4 types on an ASYMMETRIC "strong against" matrix. Riders beat Warriors & Archers, Archers beat
// Warriors & Spearmen, Warriors beat Spearmen, Spearmen beat Riders. So Archers & Riders each out-match TWO
// types (premium/fragile), Warriors & Spearmen only one (cheap/tanky), see engine.BEATS + UNIT_HP/UNIT_TURNS.
export const UNIT_ICON = ['⚔️', '🏹', '🐎', '🔱'] as const;
export const UNIT_NAME = ['Warrior', 'Archer', 'Rider', 'Spearman'] as const;
export type UnitType = 0 | 1 | 2 | 3;

export interface Unit {
  type: UnitType;
  owner: FactionId;
  movesLeft: number; // move points left this round (engine.unitMoves(type): Riders 2, everyone else 1); 0 → spent
  hp: number; // health pips (0 → destroyed); starts at engine.unitMaxHp(type), per-type, not uniform
  shot?: boolean; // Archer only: its ONE arrow this round is spent. Shooting is free of movement (an archer
  // may move and still loose, or loose and still move), so the arrow needs its own spent flag.
}

// A region's single in-progress production: a building OR a trained unit.
export type Production =
  | { kind: 'building'; building: BuildingType; turnsLeft: number; turnsTotal: number; cost: number[] }
  | { kind: 'unit'; unitType: UnitType; turnsLeft: number; turnsTotal: number; cost: number[] };

// Terrain dressing. IMPASSABLE scenery ('wall' = castle masonry, 'mountain' = natural cliffs) keeps
// owner NEUTRAL and is STRIPPED from every neighbors[] list at build time, so no unit can ever enter,
// target, or claim one (and with dominationPct=1 wins come from elimination, so unowned scenery never
// blocks victory). 'gate' tiles are normal passable tiles that grant their defender the Wall building's
// damage reduction implicitly: a gatehouse is a strong point. 'road' tiles are ordinary walkable ground
// with a road drawn across them: the land maps' pathways, purely visual. undefined = plain ground.
export type Terrain = 'wall' | 'gate' | 'mountain' | 'road';

/** Scenery no unit may ever stand on (stripped from the walkable graph at build time). */
export const impassable = (t: Terrain | undefined): boolean => t === 'wall' || t === 'mountain';

export interface Region {
  id: RegionId;
  q: number; // axial hex coord
  r: number;
  island: number; // isles: the island; land maps: the PROVINCE (faction homes + the neutral wilds)
  owner: FactionId; // NEUTRAL or a faction id
  isCapital: boolean;
  neighbors: RegionId[]; // adjacent walkable land: same island on isles maps, any adjacent land on land maps
  terrain?: Terrain; // campaign scenery (see Terrain); absent on procedural maps and old saves
  buildings: BuildingType[]; // completed buildings in this region
  production: Production | null; // the one in-progress build, if any
  unit: Unit | null; // at most one unit garrisons a region (Polytopia-style)
  reserve?: UnitType | null; // a finished soldier waiting in the barracks because the tile was occupied when
  // it was ready. It musters out at the NEXT round boundary once the tile is clear (never the instant the
  // standing unit leaves), and a tile holding a reserve may not start another unit: one in the yard, that's it.
}

// A unit crossing the sea (isles maps): it left `from` on its owner's previous turn and waits in a
// boat beside that tile, aimed at `dest`; it lands (or attacks the beach) with the normal sea-assault
// rules at the START of its owner's next turn, during the deterministic advance.
export interface Voyage {
  unit: Unit;
  owner: FactionId;
  from: RegionId; // the tile it boarded from (also the bail-out home if the landing is impossible)
  dest: RegionId; // the coastal tile it committed to
  stalled?: boolean; // the landing was blocked (a rival took the beach in peacetime) or repelled: the boat
  // holds station and its owner may pick a NEW beach for it, sailing and landing there in one action
}

export interface Island {
  id: number;
  regions: RegionId[];
  resources: readonly [number, number]; // the 2 resource types this island produces
  faction: FactionId; // home faction (island id === faction id for homes; the trailing NEUTRAL entry, the land wilds or the isles heart isle, has none)
  seaLinks: number[]; // island ids reachable by sea assault (the archipelago adjacency graph)
}

export interface Research {
  tech: string; // tech id being researched
  turnsLeft: number;
  turnsTotal: number;
  goldPerTurn: number; // Gold drained each turn while active; research stalls if it can't be paid
}

// Each AI is dealt a personality at game start that colours its war/alliance/trade choices; it still
// adapts to the board (gangs up on a runaway leader, defends when threatened) regardless of type.
export type FactionStrategy = 'aggressive' | 'defensive' | 'opportunist' | 'diplomat';

/** CAMPAIGN-ONLY weights on an AI realm, so a story level can be a lesson instead of a fair fight.
 *  Never set in Quick Battle or multiplayer: free play stays symmetric. Every field is deterministic
 *  data, so a handicapped level still simulates identically on every machine. */
export interface Handicap {
  noTech?: boolean; // never researches: no Archer, no Rider, no economy techs. Savages stay savages.
  lockUnits?: UnitType[]; // may never train these types even if somehow unlocked
  buildMul?: number; // production takes this much longer. QUANTIZED: turns are round(base * mul) over
  // base times of 2 and 3, so only 1.25 (everything +1 turn) and 1.5 (+1, and 3-turn units +2) differ.
  // Anything between them is the same board with a prettier number, so the campaign ships only those two.
}
// Three levers measured worth shipping, three measured NOT (scripts/handicaplab.ts, 12 seeds per recipe):
//   incomeMul - inert. The anti-soft-lock trickle floor refills what it takes (l01 4/12 either way).
//   capMalus  - inert to harmful. The AI is production-bound, not supply-bound: it sits on empty slots,
//               and a smaller AI army means fewer AI-vs-AI wars, so a rival farms neutral ground instead.
//   truce     - one-way, and a subsidy. It stops the AI attacking the PLAYER while the player attacks
//               freely, so the truced band spends those rounds eating neutral tiles and comes out BIGGER.

export interface Faction {
  id: FactionId;
  race: string; // race key
  name: string;
  color: number;
  isPlayer: boolean; // a HUMAN-controlled faction (exactly one in solo; any number in multiplayer)
  eliminated: boolean;
  strategy: FactionStrategy; // AI personality (unused for the player)
  noParley?: boolean; // scenario-only: this faction NEVER talks. No peace, no alliance, no trade with
  // it, by anyone. Raider bands and doomed last stands: the level is a fight, not a negotiation.
  garrison?: boolean; // scenario-only (campaign castle lords): this AI's units HOLD their walls: they
  // reposition and fight on their own ground but never step onto land they do not own, so a besieged
  // district stays besieged instead of sallying across the map. Never set in free play or multiplayer.
  aiDifficulty?: Difficulty; // per-slot AI difficulty (multiplayer host fills empty seats); falls back to g.difficulty
  handicap?: Handicap; // campaign-only weights (see Handicap); absent everywhere else
  stock: number[]; // [RESOURCES] resource stockpiles
  techs: string[]; // completed tech ids
  research: Research | null; // one active research at a time
}

export type Phase = 'select' | 'playing' | 'over';

// Diplomacy: alliances (non-aggression), declared wars (needed to attack), and trade deals
// (a recurring resource-for-resource exchange).
export type OfferKind = 'alliance' | 'trade' | 'peace';
export interface ResAmt {
  res: number; // resource index (Gold/Wood/Stone/Food)
  amt: number;
}
export interface DiploOffer {
  id: number;
  from: FactionId; // the faction proposing
  to: FactionId; // the HUMAN faction the offer is aimed at (solo: always the player)
  kind: OfferKind;
  created: number; // round the offer was made, so stale offers can expire instead of lingering forever
  give?: ResAmt; // trade only: the good the proposer hands over (one-time)
  want?: ResAmt; // trade only: the good the proposer wants back (one-time)
}
// A one-at-a-time inbox item shown to the player after the AI turn (no bombardment).
export interface DiploMessage {
  kind: 'war' | 'offer' | 'allyBroken'; // a rival declared war, an offer arrived, or an ally tore up the pact
  from: FactionId;
  to?: FactionId; // the human faction this concerns (solo: the player; clients filter to their own)
  offerId?: number; // for kind 'offer' → the pending DiploOffer
}

export interface GameStats {
  captured: number[]; // tiles captured this game, indexed by faction id
  kills: number[][]; // enemy units destroyed: kills[factionId][unitType]
  built: number[][]; // units trained: built[factionId][unitType]
}

export type Difficulty = 'normal' | 'hard'; // AI difficulty picked at match setup (tunes AI aggression + the human's handicaps)

export type GameMode = 'solo' | 'mp'; // solo keeps the single-player human perks; mp plays fully symmetric rules

export type MapKind = 'isles' | 'land'; // archipelago with sea assaults, or one connected mainland (no sea, Barracks instead of Harbor)

/** A campaign level's own victory condition, on top of the usual ones (holding dominationPct of the
 *  map, or being the last realm standing). Scenario-only: free play and multiplayer leave it unset. */
export interface Objective {
  kind: 'capture'; // take one decisive place: a keep, a fortress, a war camp
  region: RegionId;
  name: string; // what to call it in the banner, the HUD chip and the results screen
}

export interface GameState {
  round: number;
  regions: Region[];
  islands: Island[];
  factions: Faction[];
  playerFaction: FactionId;
  mapKind: MapKind; // 'isles' (default; old saves hydrate to it) or 'land'
  objective?: Objective; // campaign levels only: the one place whose capture ends the war
  voyages: Voyage[]; // units at sea (isles maps), resolved at their owner's next turn start; [] on land maps and old saves
  mode: GameMode; // balance policy switch: 'solo' = human perks on (today's tuning), 'mp' = symmetric for everyone
  turnOf: FactionId; // whose turn it is RIGHT NOW (always a human faction; solo: always the player)
  turnOrder: FactionId[]; // fixed per-game play order (shuffled at createGame); the player sits at a random slot,
  // so the factions before them open each round before the player acts, and those after close it out
  dominationPct: number; // share of the CONQUERABLE regions that wins the game (impassable scenery never counts; see engine.conquerableTotal)
  capitalRule?: boolean; // free-play CROWN RULE: each extra capital held gives +1 army cap; holding none costs -2 (scripted worlds leave it off)
  roundLimit: number; // rounds before the match is called on points. Defaults to engine.ROUND_LIMIT, but a
  // MAINLAND board holds roughly 2.4x the tiles of an archipelago one, so a land match gets a longer clock:
  // measured, every campaign land level was running out of rounds mid-war (scripts/campaignbalance.ts).
  difficulty: Difficulty; // 'normal' (today's tuning) or 'hard' (AIs press harder, human handicaps reduced)
  stats: GameStats; // per-faction running tallies this game (feed the cross-game achievement profile at game end)
  selectedRegion: RegionId | null;
  phase: Phase;
  result: 'win' | 'lose' | null; // set when phase === 'over', from the PLAYER faction's perspective (solo UI)
  winner: FactionId | null; // set when phase === 'over': who took the match (null = the solo player simply fell)
  alliances: [FactionId, FactionId][]; // normalized pairs (a < b)
  wars: [FactionId, FactionId][]; // declared wars; you can only attack a faction you are at war with
  warStamp: Record<string, number>; // "a-b" (a<b) → round war was declared (peace is locked for a few turns after)
  peaceStamp: Record<string, number>; // "a-b" (a<b) → round peace was made (war is locked for a few turns after)
  peaceAskStamp?: Record<string, number>; // "a-b" (a<b) → round an AI last ASKED a human for peace (anti-spam; optional: old saves lack it)
  allyStamp: Record<string, number>; // "a-b" (a<b) → round an alliance was broken (re-allying is locked for a few turns after)
  offers: DiploOffer[]; // pending proposals awaiting the player's accept/reject
  messages: DiploMessage[]; // new-this-turn inbox items, shown sequentially after the replay
  nextOfferId: number;
  log: LogEvent[]; // what happened during the last AI turn (shown to the player)
  replayBefore: RegionSnapshot[]; // per-region board state at the start of the last endTurn
  replay: ReplayEvent[]; // ordered AI-phase events, replayed with animation after End Turn
}

// --- turn replay: the board is rewound to `replayBefore`, then these events animate forward ---
export interface RegionSnapshot {
  owner: FactionId;
  unit: Unit | null;
  buildings: BuildingType[];
}
export type ReplayEvent =
  | {
      kind: 'unit';
      faction: FactionId;
      unitType: UnitType;
      from: RegionId;
      to: RegionId;
      combat: 'moved' | 'captured' | 'melee' | 'ranged' | 'sea' | 'sail'; // 'sail' = boarded a boat (unit leaves `from`; lands at `to` NEXT turn)
      captured: boolean;
      defenderDied: boolean;
      attackerDied: boolean;
      attackerHp: number;
      defenderHp: number;
      retaliated?: boolean; // the defender struck back (an archer duel's return volley animates from this)
    }
  | { kind: 'claim'; faction: FactionId; region: RegionId }
  | { kind: 'build'; faction: FactionId; region: RegionId; building: BuildingType }
  | { kind: 'train'; faction: FactionId; region: RegionId; unitType: UnitType }
  | { kind: 'eliminate'; faction: FactionId }
  | { kind: 'diplo'; text: string };

export type LogKind = 'attack' | 'diplo' | 'defeat' | 'info';
export interface LogEvent {
  kind: LogKind;
  text: string;
}
